import { useMemo, useState } from 'react'
import { useFirestoreQuery } from '../../firebase/useFirestore'
import useSettings from '../../hooks/Settings'
import { statusDisplaysFrom } from '../../hooks/useStatusDisplay'
import ScrollToTop from '../../hooks/ScrollToTop.js'
import PageHeader from '../../components/layout/PageHeader.jsx'
import buildStatusPeriodSummary from '../../helpers/adressHelpers/buildStatusPeriodSummary'
import resolveWervingsDiensten, { dienstNamen as toDienstNamen } from '../../helpers/wervingsacties/resolveWervingsDiensten'

/** Waar de telling 'geholpen' op leest; zie buildStatusPeriodSummary. */
const AFGEROND = 'completed'

/**
 * @param {{ code?: string, message?: string } | null} fout
 * @returns {string}
 */
const foutCode = (fout) => fout?.code || fout?.message || 'onbekende fout'

/**
 * De pagina achter de dashboardkaart 'Status + langst wachtende': per status
 * hoeveel huishoudens er nu in staan en hoeveel er in een gekozen periode in
 * kwamen, en hoeveel er in die periode zijn afgerond, per dienst.
 *
 * Nodig omdat de kaart alleen de stand van nu toont; voor een periode is de
 * statusgeschiedenis nodig. Alle telling staat in buildStatusPeriodSummary.
 *
 * Getallen pas als huishoudens, geschiedenis én instellingen er zijn: een
 * half geladen telling ziet eruit als een echte en gaat zo een verantwoording in.
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 * AI-CONTEXT: See docs/features/huishoudens-status-per-periode.md
 *
 * @returns {JSX.Element}
 */
const StatusPerPeriode = () => {
    ScrollToTop()
    const jaar = new Date().getFullYear()
    const [van, setVan] = useState(`${jaar}-01-01`)
    const [tot, setTot] = useState(`${jaar}-12-31`)
    const [fout, setFout] = useState(/** @type {{ code?: string, message?: string } | null} */ (null))

    const settings = useSettings()
    // Eigen listeners en niet useSharedCollection (die de dashboardkaart
    // gebruikt): die meldt een mislukte read alleen in de console, en dan bleef
    // deze pagina eeuwig op 'laden' staan. Kaart en pagina staan nooit tegelijk open.
    const adressen = useFirestoreQuery('Adresses', { initial: null, onError: setFout })
    const history = useFirestoreQuery('StatusHistory', {
        where: [['EntityType', '==', 'adress']],
        initial: null,
        onError: setFout,
    })

    // Op de losse velden en niet op `settings`: useSettings geeft elke render een
    // nieuw object, dan zou de memo nooit iets bewaren.
    const { wervingsDienstenConfig, adressStatusConfig, loaded } = settings
    const statusLabels = useMemo(() => statusDisplaysFrom({ adressStatusConfig }).adress.labels, [adressStatusConfig])
    const diensten = useMemo(() => resolveWervingsDiensten({ WervingsDiensten: wervingsDienstenConfig }), [wervingsDienstenConfig])
    const dienstNamen = useMemo(() => toDienstNamen({ WervingsDiensten: wervingsDienstenConfig }), [wervingsDienstenConfig])

    const geladen = Array.isArray(adressen) && Array.isArray(history) && loaded === true
    // `yyyy-mm-dd` sorteert als tekst gelijk aan de datum.
    const periodeFout = !van || !tot
        ? "Kies bij 'Van' en 'Tot en met' een datum."
        : tot < van ? "'Tot en met' ligt vóór 'Van'. Kies een latere einddatum." : ''

    const telling = useMemo(
        () => (geladen && !periodeFout
            ? buildStatusPeriodSummary({ adressen, history, from: van, to: tot, diensten, dienstNamen })
            : null),
        [geladen, periodeFout, adressen, history, van, tot, diensten, dienstNamen],
    )

    const afgerondLabel = statusLabels[AFGEROND]

    const inhoud = () => {
        if (fout) {
            return (
                <p className='status-periode__error' role='alert'>
                    {`De gegevens konden niet geladen worden (${foutCode(fout)}). Herlaad de pagina om het opnieuw te proberen.`}
                </p>
            )
        }
        if (!geladen) return <p className='status-periode__hint'>Gegevens laden…</p>
        if (periodeFout) return <p className='status-periode__hint'>{periodeFout}</p>

        return (
            <>
                <div className='dashboard-container'>
                    <h2>Per status</h2>
                    <table aria-label='Per status' className='status-periode__table'>
                        <thead>
                            <tr>
                                <th>STATUS</th>
                                <th>NU</th>
                                <th>IN DE PERIODE IN DEZE STATUS GEKOMEN</th>
                            </tr>
                        </thead>
                        <tbody>
                            {telling.perStatus.map((s) => (
                                <tr key={s.status}>
                                    <td>{statusLabels[s.status] || s.status}</td>
                                    <td>{s.nu}</td>
                                    <td>{s.inPeriode}</td>
                                </tr>
                            ))}
                            {/* Elk huishouden één keer: de kolom erboven kan hoger
                                optellen als een huishouden door meer statussen ging. */}
                            <tr className='status-periode__total'>
                                <td>Totaal</td>
                                <td>{telling.totaal.nu}</td>
                                <td>{telling.totaal.inPeriode}</td>
                            </tr>
                        </tbody>
                    </table>
                    {telling.afgerondZonderDatum > 0 && (
                        <p className='status-periode__hint'>
                            {`${telling.afgerondZonderDatum} ${telling.afgerondZonderDatum === 1 ? 'huishouden staat' : 'huishoudens staan'} op '${afgerondLabel}' zonder datum. `}
                            Die zijn afgerond voordat de app dat bijhield, en tellen in geen periode mee.
                        </p>
                    )}
                </div>

                <div className='dashboard-container'>
                    <h2>{`${afgerondLabel} in de periode, per dienst`}</h2>
                    <table aria-label='Afgerond per dienst' className='status-periode__table'>
                        <thead>
                            <tr>
                                <th>DIENST</th>
                                <th>HUISHOUDENS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {telling.afgerond.perDienst.map((d) => (
                                <tr key={d.key}>
                                    <td>{d.label}</td>
                                    <td>{d.aantal}</td>
                                </tr>
                            ))}
                            <tr>
                                <td>Zonder dienst</td>
                                <td>{telling.afgerond.zonderDienst}</td>
                            </tr>
                            <tr className='status-periode__total'>
                                <td>Totaal</td>
                                <td>{telling.afgerond.totaal}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </>
        )
    }

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader heading='Huishoudens per status' />
                <div className='dashboard-container status-periode__periode'>
                    <label htmlFor='status-periode-van'>Van</label>
                    <input id='status-periode-van' type='date' value={van} onChange={(e) => setVan(e.target.value)} />
                    <label htmlFor='status-periode-tot'>Tot en met</label>
                    <input id='status-periode-tot' type='date' value={tot} onChange={(e) => setTot(e.target.value)} />
                </div>
                {inhoud()}
            </div>
        </div>
    )
}

export default StatusPerPeriode
